import { getDocs, collection, query, where, addDoc, setDoc, doc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { getRoutePartners, getOnlySuites } from "../../../helper/functions";

/**
 * Fetch all route partners for dropdown selection
 * @returns {Promise<Array>} Array of route partners
 */
export const fetchRoutePartners = async () => {
  try {
    const partners = await getRoutePartners([]);
    return partners;
  } catch (error) {
    console.error("Error fetching route partners:", error);
    throw error;
  }
};

/**
 * Fetch location data (countries and states) from Firestore
 * @returns {Promise<Array>} Array of location data
 */
export const fetchLocationData = async () => {
  try {
    const docRef = collection(db, "countries");
    const querySnapshot = await getDocs(docRef);
    const countriesData = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return countriesData[0]?.locationData || [];
  } catch (error) {
    console.error("Error fetching location data:", error);
    throw error;
  }
};

/**
 * Get states for a specific country
 * @param {string} countryCode - The country code
 * @param {Array} locationData - The location data array
 * @returns {Array} Array of states for the country
 */
export const getStatesForCountry = (countryCode, locationData) => {
  try {
    const countryData = locationData.find(
      (country) => country.code === countryCode
    );
    return countryData ? countryData.states || [] : [];
  } catch (error) {
    console.error("Error getting states for country:", error);
    return [];
  }
};

/**
 * Fetch all shipping warehouses
 * @param {Object} conditions - Query conditions
 * @returns {Promise<Array>} Array of warehouses
 */
export const fetchShippingWarehouses = async (conditions = {}) => {
  try {
    const querySnapshot = collection(db, "routePartnerMainAddress");
    const whereConditions = Object.keys(conditions).map((key) =>
      where(key, "==", conditions[key])
    );
    const q = query(querySnapshot, ...whereConditions);
    const snapshot = await getDocs(q);
    const warehouses = [];
    snapshot.forEach((doc) => {
      warehouses.push({ ...doc.data(), id: doc.id });
    });
    return warehouses;
  } catch (error) {
    console.error("Error fetching shipping warehouses:", error);
    throw error;
  }
};

/**
 * Get transit day range options
 * @returns {Array} Array of transit day range options
 */
export const getTransitDayOptions = () => {
  return [
    { value: "10-20", label: "10-20 days" },
    { value: "20-30", label: "20-30 days" },
    { value: "30-40", label: "30-40 days" },
    { value: "40-50", label: "40-50 days" },
    { value: "50-60", label: "50-60 days" },
  ];
};

/**
 * Get shipping service options
 * @returns {Array} Array of shipping service options
 */
export const getShippingServiceOptions = () => {
  return [
    { value: "standard", label: "Standard Shipping Service" },
    { value: "crowdshipping", label: "This is a Crowdshipping Service" },
    { value: "traveler", label: "This is a Traveler Service" },
  ];
};

/**
 * Create a new shipping service in Firestore
 * @param {Object} data - The shipping service data
 * @returns {Promise<Object>} The new document data
 */
export const updateShippingService = async (id, data) => {
  try {
    await setDoc(doc(db, "shippingServices", id), data, { merge: true });
    return { ...data, id };
  } catch (error) {
    console.error("Error updating shipping service:", error);
    throw error;
  }
};
export const createShippingService = async (data) => {
  try {
    if(data.id){
      const response = await updateShippingService(data.id, data);
      return response;
    }
    else{
      const docRef = await addDoc(collection(db, "shippingServices"), data);
      return { ...data,id: docRef.id };
    }
  } catch (error) {
    console.error("Error creating shipping service:", error);
    throw error;
  }
};

/**
 * Update an existing shipping service in Firestore
 * @param {string} id - The document ID
 * @param {Object} data - The updated data
 * @returns {Promise<void>}
 */


// Fetch suites for standard shipping type
export const fetchSuitesByWarehouseId = async (warehouseId) => {
  return await getOnlySuites([
    { key: 'routePartnerMainAddressId', operator: '==', value: warehouseId }
  ]);
};

// Fetch muse partners for crowdshipping or traveler
export const fetchMusePartnersByType = async (routePartnerId, partnerType) => {
  const q = query(
    collection(db, 'musePartners'),
    where('routePartnerId', '==', routePartnerId),
    where('partnerType', '==', partnerType)
  );
  const snapshot = await getDocs(q);
  const arr = [];
  snapshot.forEach(doc => arr.push({ ...doc.data(), id: doc.id }));
  return arr;
}; 

export const addedByConversion = (role)=>{
  if(role == "Superadmin"){
      return "SUPER_ADMIN"
  }
  else if(role == "Route Partner Admin"){
      return "ROUTE_PARTNER_ADMIN"
  }
  else{
      return ""
  }
}